import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpTag, SearchField, TableScroll } from '@hp/ui';
import { trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { fmtInt } from '../../lib/format';
import { invalidateMapping, useCampaignOptions, type CampaignMapRow } from './queries';

/** Search-as-you-type picker over the CRM campaign options in use; picking decides the row. */
function CrmCampaignPicker({ onPick, onCancel }: { onPick: (optionId: string) => void; onCancel: () => void }) {
  const { t } = useTranslation(['performance', 'common']);
  const [q, setQ] = useState('');
  const options = useCampaignOptions(q, true);
  return (
    <div className="bo-sections bo-picker">
      <SearchField
        autoFocus
        value={q}
        placeholder={t('mapping.pickerPlaceholder')}
        onChange={(e) => setQ(e.target.value)}
        aria-label={t('mapping.pickerPlaceholder')}
      />
      <ul className="bo-picker__list">
        {(options.data ?? []).map((o) => (
          <li key={o.id}>
            <button type="button" className="bo-picker__option" onClick={() => onPick(o.id)}>
              <span>{o.label}</span>
              <small className="bo-muted">{t('mapping.casesCount', { count: o.cases })}</small>
            </button>
          </li>
        ))}
        {options.data && options.data.length === 0 && <li className="bo-muted">{t('mapping.pickerEmpty')}</li>}
      </ul>
      <HpButton variant="quiet" className="bo-button--sm" onClick={onCancel}>
        {t('common:actions.cancel')}
      </HpButton>
    </div>
  );
}

function StatusTag({ status }: { status: CampaignMapRow['status'] }) {
  const { t } = useTranslation(['performance']);
  if (status === null) return <HpTag tone="neutral">{t('mapping.status.unmapped')}</HpTag>;
  const tone = status === 'confirmed' ? 'success' : status === 'suggested' ? 'warning' : 'neutral';
  return <HpTag tone={tone}>{t(`mapping.status.${status}`)}</HpTag>;
}

/** One ad campaign per row: its spend, the CRM campaign it is (or is suggested to be), and the decision buttons. */
export function CampaignMapTable({ rows, canManage }: { rows: CampaignMapRow[]; canManage: boolean }) {
  const { t } = useTranslation(['performance', 'common']);
  const [picking, setPicking] = useState<number | null>(null);
  const decide = useMutation(
    trpc.performance.mapping.decideCampaign.mutationOptions({
      onSuccess: () => {
        setPicking(null);
        return invalidateMapping();
      },
    }),
  );

  return (
    <>
      <ErrorAlert error={decide.error} className="bo-alert--inset" />
      <TableScroll label={t('mapping.campaigns.title')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('mapping.columns.adCampaign')}</th>
            <th className="bo-num">{t('mapping.columns.spend')}</th>
            <th>{t('mapping.columns.days')}</th>
            <th>{t('mapping.columns.crmCampaign')}</th>
            <th>{t('mapping.columns.status')}</th>
            {canManage && <th>{t('mapping.columns.actions')}</th>}
          </BrutTheadRow>
          <tbody>
            {rows.map((r) => (
              <tr key={r.adCampaignId}>
                <td>
                  <span className="bo-row">
                    <HpTag tone="info">{t(`channel.${r.provider}`)}</HpTag>
                    <span className="bo-strong">{r.name}</span>
                  </span>
                  <br />
                  <small className="bo-muted">{r.accountName}</small>
                </td>
                <td className="bo-num">{fmtInt(Math.round(r.costCzk))}</td>
                <td className="bo-mono bo-muted">
                  {r.firstDay} – {r.lastDay}
                </td>
                <td>
                  {picking === r.adCampaignId ? (
                    <CrmCampaignPicker
                      onPick={(optionId) => decide.mutate({ adCampaignId: r.adCampaignId, crmOptionId: optionId })}
                      onCancel={() => setPicking(null)}
                    />
                  ) : r.status === 'rejected' ? (
                    <span className="bo-muted">{t('mapping.noCrmCampaign')}</span>
                  ) : r.crmLabel ? (
                    <span>
                      {r.crmLabel}
                      {r.score !== null && r.status === 'suggested' && (
                        <small className="bo-muted"> {t('mapping.score', { pct: Math.round(r.score * 100) })}</small>
                      )}
                    </span>
                  ) : (
                    <span className="bo-muted">—</span>
                  )}
                </td>
                <td>
                  <StatusTag status={r.status} />
                </td>
                {canManage && (
                  <td>
                    <span className="bo-row">
                      {r.status === 'suggested' && r.crmOptionId && (
                        <HpButton
                          variant="secondary"
                          icon="check"
                          className="bo-button--sm"
                          busy={decide.isPending && decide.variables?.adCampaignId === r.adCampaignId}
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ adCampaignId: r.adCampaignId, crmOptionId: r.crmOptionId })}
                        >
                          {t('common:actions.confirm')}
                        </HpButton>
                      )}
                      {picking !== r.adCampaignId && (
                        <HpButton
                          variant="quiet"
                          icon="edit"
                          className="bo-button--sm"
                          onClick={() => setPicking(r.adCampaignId)}
                        >
                          {t('mapping.actions.change')}
                        </HpButton>
                      )}
                      {r.status !== 'rejected' && (
                        <HpButton
                          variant="quiet"
                          icon="close"
                          className="bo-button--sm"
                          busy={
                            decide.isPending &&
                            decide.variables?.adCampaignId === r.adCampaignId &&
                            decide.variables.crmOptionId === null
                          }
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ adCampaignId: r.adCampaignId, crmOptionId: null })}
                        >
                          {t('mapping.actions.none')}
                        </HpButton>
                      )}
                    </span>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={canManage ? 6 : 5} className="bo-cell-empty">
                  {t('mapping.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
    </>
  );
}
